import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Coffee as CoffeeIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import api from '../services/api';

const ForgotPassword = ({keywordsData,language}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await api.post('/password-reset/request', { email });
      setSuccess(true);
      
      // Enable resend button after 60 seconds
      setResendDisabled(true);
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err) {
      setError(err.response?.data?.message || language ==='ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى':'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendDisabled) return;
    
    setLoading(true);
    try {
      await api.post('/password-reset/resend', { email });
      
      // Reset timer
      setResendDisabled(true);
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err) {
      setError(err.response?.data?.message || language ==='ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى':'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ 
      py: 8,
      px: 2,
      mb:10,
      backgroundColor: '#000000',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Paper sx={{ 
        p: { xs: 3, sm: 5 },
        width: '100%',
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(255, 215, 0, 0.15)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          opacity: 0.1,
          transform: 'rotate(15deg)'
        }}>
          <CoffeeIcon sx={{ fontSize: 80, color: '#FFD700' }} />
        </Box>

        {/* Back to Login Link */}
        <Box sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: '#B3B3B3',
              textDecoration: 'none',
              fontSize: '0.95rem',
              '&:hover': {
                color: '#FFD700'
              }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
            {language ==='ar' ?  'العودة إلى تسجيل الدخول' : 'Return to login'}
          </Link>
        </Box>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            border: '2px solid #FFD700'
          }}>
            <EmailIcon sx={{ fontSize: 40, color: '#FFD700' }} />
          </Box>
          <Typography variant="h4" sx={{ 
            color: '#FFD700',
            fontWeight: 900,
            letterSpacing: '2px',
            mb: 1
          }}>
            {language ==='ar' ?  'نسيت كلمة المرور؟' : 'Forgot password?'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
            {language ==='ar' ?  'لا تقلق! سنرسل لك رابط إعادة تعيين كلمة المرور' : 'Dont worry! Well send you a password reset link.'}
          </Typography>
        </Box>

        {/* Success Message */}
        {success && (
          <Alert 
            severity="success"
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              color: '#4CAF50',
              border: '1px solid #4CAF50',
              borderRadius: '12px',
              '& .MuiAlert-icon': {
                color: '#4CAF50'
              }
            }}
          >
            {language ==='ar' ?  'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني' : 'A password reset link has been sent to your email.'}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert 
            severity="error"
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(255, 61, 0, 0.1)',
              color: '#FF3D00',
              border: '1px solid #FF3D00',
              borderRadius: '12px',
              '& .MuiAlert-icon': {
                color: '#FF3D00'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#B3B3B3', mb: 1, textAlign: 'left' }}>
              {language ==='ar' ?  'البريد الإلكتروني' : 'E-mail'}
            </Typography>
            <TextField
              fullWidth
              placeholder={language ==='ar' ?  'أدخل بريدك الإلكتروني' : 'Enter your E-mail'}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || success}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: '#2D2D2D',
                  },
                  '&:hover fieldset': {
                    borderColor: '#FFD700',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#FFD700',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#6B6B6B',
                  opacity: 1,
                },
                '& .Mui-disabled': {
                  opacity: 0.7,
                }
              }}
            />
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || success}
            sx={{ 
              py: 1.8,
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              fontSize: '1.1rem',
              borderRadius: '12px',
              border: '1px solid #FFD700',
              '&:hover': {
                backgroundColor: '#FFE55C',
                boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                transform: 'translateY(-2px)'
              },
              '&.Mui-disabled': {
                backgroundColor: '#2D2D2D',
                color: '#6B6B6B',
                border: '1px solid #2D2D2D',
              },
              transition: 'all 0.2s ease'
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: '#000000' }} />
                <span>{language ==='ar' ?  'جاري الإرسال...' : 'Sending ...'}</span>
              </Box>
            ) : (
              language ==='ar' ?  'إرسال رابط إعادة التعيين' : 'Send reset link'
            )}
          </Button>

          {/* Resend Button */}
          {success && (
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResend}
              disabled={resendDisabled || loading}
              sx={{ 
                mt: 2,
                py: 1.5,
                color: resendDisabled ? '#6B6B6B' : '#FFD700',
                borderColor: resendDisabled ? '#2D2D2D' : '#FFD700',
                borderRadius: '12px',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#FFE55C',
                  backgroundColor: 'rgba(255, 215, 0, 0.08)',
                  color: '#FFE55C',
                },
                '&.Mui-disabled': {
                  borderColor: '#2D2D2D',
                  color: '#6B6B6B',
                }
              }}
            >
              {resendDisabled ? language ==='ar' ?  `إعادة الإرسال بعد ${countdown} ثانية` : `Resend after ${countdown} seconds` : language ==='ar' ?  'إعادة إرسال الرابط' : 'Resend link'}
            </Button>
          )}
        </Box>

        {/* Help Box */}
        <Box sx={{ 
          mt: 4,
          p: 2,
          backgroundColor: '#1A1A1A',
          borderRadius: '12px',
          border: '1px solid #2D2D2D'
        }}>
          <Typography variant="body2" sx={{ color: '#B3B3B3', textAlign: 'center' }}>
            <span style={{ color: '#FFD700', fontWeight: 700 }}>{language ==='ar' ?  '💡 نصيحة:' : '💡 Tip:'}</span>
            {' '}
            {language ==='ar' ?  'تحقق من مجلد البريد الغير مرغوب (Spam) إذا لم يصلك البريد الإلكتروني خلال دقائق' : 'Check your spam folder if you have not received the email within minutes.'}
          </Typography>
        </Box>

        {/* Sign Up Link */}
        <Box sx={{ 
          mt: 4, 
          pt: 3, 
          textAlign: 'center',
          borderTop: '1px solid #2D2D2D'
        }}>
          <Typography variant="body2" sx={{ color: '#B3B3B3', display: 'inline' }}>
            {language ==='ar' ?  'لا تمتلك حساب؟' : 'Dont have an account?'} {' '}
          </Typography>
          <Link
            component={RouterLink}
            to="/register"
            sx={{
              color: '#FFD700',
              fontWeight: 700,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            {language ==='ar' ?  'سجل الآن' : 'Register'}
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;